/**
 * Navigation Store (Pinia)
 *
 * @version 1.0.0
 * @description Manages navigation areas, company switching, route-based toolbar.
 * @migrated-from store/navigation/navigation.ts (Vuex 4)
 *
 * Cross-store dependencies (called lazily inside actions — no circular import risk):
 *   - useAuthStore       → user.role, user.sectors
 *   - useAppStore         → getUserPreference(), setUserPreference()
 *   - useProspectsStore   → fetchHighlightedCount()
 *   - useCustomersStore   → fetchHighlightedCount()
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import router from '@/router'
import { useAuthStore } from '@/stores/auth'
import { useAppStore } from '@/stores/app'
import { useProspectsStore } from '@/stores/useProspectsStore'
import { useCustomersStore } from '@/stores/useCustomersStore'

// =============================================================================
// Types
// =============================================================================

export interface NavigationItem {
  title: string
  route?: string
  subItems?: NavigationItem[]
  /** R1 — la voce è visibile solo se l'utente ha ALMENO UNO di questi permessi */
  permissionsAny?: string[]
}

export type CompanyCode = 'mkt' | 'srv'
export type NavigationArea = 'idle' | 'admin' | 'commercial' | 'tech' | 'tech_strict' | 'cst'

// =============================================================================
// Static Navigation Menus
// =============================================================================

const NAVIGATION_MENUS: Record<string, NavigationItem[]> = {
  idle: [],
  admin: [
    {
      title: 'UTENTI',
      route: 'users',
    },
    {
      title: 'RUOLI',
      route: 'roles',
      permissionsAny: ['roles.view', 'roles.manage'],
    },
    {
      title: 'EVENTI',
      route: 'notification-events',
      permissionsAny: ['notifications.rules.manage'],
    },
    {
      title: 'TEMPLATE PDF',
      route: 'pdf-templates',
      permissionsAny: ['pdf.templates.manage'],
    },
    {
      title: 'SETTORI',
      route: 'sectors',
    },
    {
      title: 'ASSOCIAZIONI',
      route: 'associations-provinces',
      /*subItems: [
        {
          title: 'PROVINCE',
          route: 'associations-provinces',
        },
      ],*/
    },
    {
      title: 'CONFIGURATORE AR',
      route: 'ar-products',
      subItems: [
        {
          title: 'PRODOTTI',
          route: 'ar-products',
        },
        {
          title: 'GRUPPI',
          route: 'ar-product-groups',
        },
        {
          title: 'CONTROPARETI',
          route: 'ar-wall-claddings',
        },
        {
          title: 'COMBINAZIONI',
          route: 'ar-combinations',
        },
      ],
    },
    {
      title: 'PREVENTIVI',
      route: 'standard-quotes',
      /*ubItems: [
        {
          title: 'STANDARD',
          route: 'standard-quotes',
        },
      ],*/
    },
    {
      title: 'REPORT',
      route: 'reports',
      subItems: [
        {
          title: 'Attivitá agenti',
          route: 'agents-activity',
        },
      ],
    },
  ],
  commercial: [
    {
      title: 'AGENDA',
      route: 'agenda',
    },
    {
      title: 'ANAGRAFICA',
      route: 'contacts',
      subItems: [
        {
          title: 'CLIENTI',
          route: 'customers',
        },
        {
          title: 'PROSPECT',
          route: 'prospects',
        },
        {
          title: 'CENSIMENTI',
          route: 'censuses',
        },
      ],
    },
    {
      title: 'PRODOTTI',
      route: 'products',
      subItems: [
        {
          title: 'ANAGRAFICA',
          route: 'products',
        },
        {
          title: 'MARCHI',
          route: 'horeca-brands',
        },
        {
          title: 'CATEGORIE',
          route: 'horeca-categories',
        },
      ],
    },
    {
      title: 'PROGETTI AR',
      route: 'ar-projects',
    },
    {
      title: 'PREVENTIVI',
      route: 'quotes',
    },
    {
      // R2 — dopo PREVENTIVI (decisione Enrico). L'admin la vede in entrambe
      // le aree automaticamente: il gating è sui permessi, non sull'area.
      title: 'COMMESSE',
      route: 'commesse',
      permissionsAny: ['commesse.view.all', 'commesse.view.macroarea'],
    },
  ],
  tech: [
    {
      // R2 — prima di RAPPORTINI (decisione Enrico)
      title: 'COMMESSE',
      route: 'tech-commesse',
      permissionsAny: ['commesse.view.all', 'commesse.view.macroarea'],
    },
    {
      title: 'RAPPORTINI',
      route: 'interventions',
    },
    {
      title: 'MATERIALI',
      route: 'tech-products',
    },
    {
      title: 'CLIENTI',
      route: 'tech-customers',
    },
    {
      title: 'MACCHINE',
      route: 'machines-data',
      // R1 (19/08, de-roling): sotto-voci catalogo su machines.catalog.manage
      // (solo admin). Per gli altri resta la sola ANAGRAFICA → la regola di
      // collasso (1 sotto-voce → link diretto) riproduce il vecchio tech_strict.
      subItems: [
        { title: 'ANAGRAFICA', route: 'machines-data' },
        { title: 'BRAND', route: 'machines-manufacturers', permissionsAny: ['machines.catalog.manage'] },
        { title: 'MODELLI', route: 'machines-models', permissionsAny: ['machines.catalog.manage'] },
        { title: 'OPERAZIONI', route: 'machines-operations', permissionsAny: ['machines.catalog.manage'] },
        { title: 'TIPI DI PROBLEMA', route: 'machines-problems', permissionsAny: ['machines.catalog.manage'] },
      ],
    },
    {
      title: 'TECNICI',
      route: 'technicians-data',			// DETTAGLI PER ENTRAMBI: carta bianca
      // R1 (19/08, decisione Enrico): chi entra qui li gestisce →
      // technicians.manage (admin + tech-boss dalla migration 2026_08_19_180000)
      permissionsAny: ['technicians.manage'],
      subItems: [
        {
          title: 'ANAGRAFICA',
          route: 'technicians-data',
        },
        {
          title: 'MEZZI DI TRASPORTO',
          route: 'technicians-cars',
        },
      ],
    },
    {
      title: 'REPORT',
      route: 'reports',
      // R1 (19/08): voce permission-driven — admin e tech-boss hanno
      // reports.tech.view/occupation, il tech semplice no.
      permissionsAny: ['reports.tech.view', 'reports.tech.occupation'],
      subItems: [
        { title: 'ORE LAVORO', route: 'tech-reports-working-hours' },
        { title: 'SPESE', route: 'tech-reports-costs' },
        { title: 'ESPORTA', route: 'tech-reports-export' },
        { title: 'OCCUPAZIONE TECNICI', route: 'tech-reports-occupation' },
      ],
    },
  ],
  // R1 (19/08, de-roling): tech_strict ELIMINATO — il menu ridotto dei tecnici
  // semplici emerge dai permessi (TECNICI→technicians.manage, REPORT→reports.*,
  // sotto-voci MACCHINE→machines.catalog.manage + regola di collasso).
  cst: [
    {
      title: 'RAPPORTINI',
      route: 'cst-interventions',
    },
  ],
}

const ALL_COMPANIES: CompanyCode[] = ['mkt', 'srv']

// =============================================================================
// Store
// =============================================================================

export const useNavigationStore = defineStore('navigation', () => {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const currentRoute = ref<string | null>(null)
  const availableAreas = ref<NavigationArea[]>([])
  const currentNavigation = ref<NavigationArea>('admin')
  const availableCompanies = ref<CompanyCode[]>([])
  const currentCompany = ref<CompanyCode>('mkt')

  // ---------------------------------------------------------------------------
  // Getters
  // ---------------------------------------------------------------------------

  /** Full API URL with company prefix (e.g. `https://api.example.com/mkt`) */
  const companyApiUrl = computed<string>(
    () => `${import.meta.env.VITE_API_URL}/${currentCompany.value}`
  )

  /**
   * Filtro permessi (R1): tiene solo le voci per cui l'utente ha almeno uno
   * dei permessi in `permissionsAny` (voci senza campo: sempre visibili).
   * 19/08: filtra anche le SOTTO-VOCI — con una sola sotto-voce residua la
   * regola di collasso dei componenti la trasforma in link diretto.
   */
  function filterItemsByPermissions(items: NavigationItem[]): NavigationItem[] {
    const authStore = useAuthStore()
    return items
      .filter((item) => !item.permissionsAny || authStore.canAny(...item.permissionsAny))
      .map((item) => item.subItems
        ? { ...item, subItems: item.subItems.filter((s) => !s.permissionsAny || authStore.canAny(...s.permissionsAny)) }
        : item)
  }

  /**
   * Voci di menu di un'area, già filtrate sui permessi (R1).
   * UNICO punto di consumo corretto di NAVIGATION_MENUS per il render:
   * AppNavigation e AppToolbar devono passare da qui, non dal raw `navigations`
   * (fix 19/08: la voce RUOLI restava visibile agli admin senza roles.view).
   */
  function itemsForArea(area: string): NavigationItem[] {
    return filterItemsByPermissions(NAVIGATION_MENUS[area] ?? [])
  }

  /** Navigation menu items for the current area (filtrate sui permessi, R1) */
  const currentNavigationItems = computed<NavigationItem[]>(() =>
    itemsForArea(currentNavigation.value),
  )

  /** All available navigation menus (static reference) */
  const navigations = NAVIGATION_MENUS

  // ---------------------------------------------------------------------------
  // Actions — Company
  // ---------------------------------------------------------------------------

  /**
   * Switch active company (mkt/srv).
   * Persists to user preferences and refreshes highlighted counts for commercial.
   */
  function changeCompany(company: CompanyCode): void {
    if (company === currentCompany.value) return
    if (!availableCompanies.value.includes(company)) return

    currentCompany.value = company

    // Persist preference (lazy store call — no circular dep)
    const appStore = useAppStore()
    appStore.setUserPreference('navigationCompany', company)

    // Refresh highlighted counts for commercial users
    const authStore = useAuthStore()
    if (authStore.user?.role === 'commercial') {
      _refreshHighlightedCounts()
    }
  }

  /**
   * Switch active navigation area.
   * Navigates to first route of the new area.
   *
   * IMPORTANT: Company is changed AFTER navigation completes.
   * This prevents useListPage's company watcher (on the OLD page)
   * from firing a redirect that cancels the navigation.
   */
  async function changeArea(area: NavigationArea | null, routerInstance?: typeof router): Promise<void> {
    const nav = routerInstance ?? router

    if (area == null || (area !== currentNavigation.value && NAVIGATION_MENUS[area] != null)) {
      currentNavigation.value = area ?? 'idle'

      const appStore = useAppStore()
      appStore.setUserPreference('navigationArea', area)

      // R1: prima rotta dell'area scelta tra le voci VISIBILI per permessi
      const menuItems = itemsForArea(area ?? '')
      if (menuItems?.length) {
        // Navigate to first valid route in the area
        const firstNav = menuItems[0]
        const targetRoute = firstNav.route ?? firstNav.subItems?.[0]?.route
        if (targetRoute) {
          // Navigate FIRST — old component unmounts and its watchers are destroyed
          await nav.push({ name: targetRoute })
        }

        // NOW switch company — old useListPage company watcher is gone
        if (area === 'tech' && availableCompanies.value.includes('srv')) {
          changeCompany('srv')
        }
        if (area === 'commercial' && availableCompanies.value.includes('mkt')) {
          changeCompany('mkt')
        }
      } else {
        const currentRouteName = router.currentRoute.value?.name as string | undefined
        if (currentRouteName && currentRouteName !== 'dashboard') {
          nav.push({ name: 'dashboard' })
        }
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Actions — Toolbar / Navigation update
  // ---------------------------------------------------------------------------

  /**
   * Detect and set current navigation area based on active route.
   * Falls back to user preference, then first available area.
   */
  function updateToolbar(): void {
    const routeName = (router.currentRoute.value?.name as string) ?? null
    const fullPath = router.currentRoute.value?.fullPath ?? ''

    let currentArea: NavigationArea | null = null

    // 1. Check if current route matches any available area
    for (const area of availableAreas.value) {
      if ((routeName != null && routeName === area) || (fullPath && fullPath.indexOf(area) === 1)) {
        currentArea = area
        break
      }
    }

    // 2. Check user preferences
    if (currentArea == null) {
      const appStore = useAppStore()
      const preferredArea = appStore.getUserPreference('navigationArea') as NavigationArea | null

      if (preferredArea != null && NAVIGATION_MENUS[preferredArea]) {
        for (const page of NAVIGATION_MENUS[preferredArea]) {
          if (page.route && fullPath && fullPath.indexOf(page.route) === 1) {
            currentArea = preferredArea
            break
          }
          if (page.subItems) {
            for (const sub of page.subItems) {
              if (sub.route && fullPath && fullPath.indexOf(sub.route) === 1) {
                currentArea = preferredArea
                break
              }
            }
            if (currentArea) break
          }
        }
      }
    }

    // 3. Fallback to first available area
    if (currentArea == null && availableAreas.value.length > 0) {
      currentArea = availableAreas.value[0]
    }

    currentNavigation.value = currentArea ?? 'idle'
  }

  /**
   * Compute available areas and companies based on authenticated user's role.
   * Called after login, /me, and session restore.
   */
  function updateNavigation(): void {
    const authStore = useAuthStore()
    const user = authStore.user

    if (user == null) {
      availableAreas.value = []
      currentNavigation.value = 'idle'
      return
    }

    let areas: NavigationArea[] = []
    let companies: CompanyCode[] = []

    switch (user.role) {
      case 'admin':
      case 'super-admin':
        areas = ['admin', 'commercial', 'tech']
        companies = [...ALL_COMPANIES]
        break
      case 'commercial':
        if (user.sectors?.length) {
          areas = ['commercial']
          for (const s of user.sectors) {
            const comp = s.company?.toLowerCase() as CompanyCode | undefined
            if (comp && !companies.includes(comp)) {
              companies.push(comp)
            }
          }
        }
        break
      case 'tech':
        areas = ['tech']
        companies = ['srv']
        break
      case 'cst':
        areas = ['cst']
        companies = ['srv']
        break
    }

    availableAreas.value = areas
    availableCompanies.value = companies

    // Restore preferred company from user preferences
    const appStore = useAppStore()
    const savedCompany = appStore.getUserPreference('navigationCompany') as CompanyCode | null
    if (savedCompany != null && companies.includes(savedCompany)) {
      currentCompany.value = savedCompany
    } else if (companies.length > 0) {
      currentCompany.value = companies[0]
    }

    // Refresh highlighted counts for commercial users
    if (user.role === 'commercial') {
      _refreshHighlightedCounts()
    }
  }

  // ---------------------------------------------------------------------------
  // Setters (Vuex mutation compat)
  // ---------------------------------------------------------------------------

  function setCurrentRoute(route: any): void {
    // Extract fullPath string from RouteLocationNormalized (or accept plain string)
    currentRoute.value = typeof route === 'string' ? route : route?.fullPath ?? null
  }

  function setCurrentNavigation(area: NavigationArea): void {
    currentNavigation.value = area
  }

  function setCurrentCompany(company: CompanyCode): void {
    currentCompany.value = company
  }

  function setAvailableAreas(areas: NavigationArea[]): void {
    availableAreas.value = areas
  }

  function setAvailableCompanies(companies: CompanyCode[]): void {
    availableCompanies.value = companies
  }

  // ---------------------------------------------------------------------------
  // Internal — lazy import per evitare circular deps
  // ---------------------------------------------------------------------------

  function _refreshHighlightedCounts(): void {
    useProspectsStore().fetchHighlightedCount().catch(() => {})
    useCustomersStore().fetchHighlightedCount().catch(() => {})
  }

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    // State
    currentRoute,
    availableAreas,
    currentNavigation,
    availableCompanies,
    currentCompany,

    // Getters
    companyApiUrl,
    currentNavigationItems,
    itemsForArea,
    navigations,

    // Actions
    changeCompany,
    changeArea,
    updateToolbar,
    updateNavigation,

    // Setters (Vuex mutation compat)
    setCurrentRoute,
    setCurrentNavigation,
    setCurrentCompany,
    setAvailableAreas,
    setAvailableCompanies,
  }
})
